import { isNil, minBy, orderBy } from "lodash"
import { Q_FROM_ORDINAL, Q_ORDINAL, Q_VENUE } from "constant"
import { pluralize } from "utils/string"
import {
  filterLevelsByFeatureOrdinals,
  getFeatureOrdinal,
  getVenueLevels,
} from "utils/feature"
import { useVenueController } from "hooks/venue"
import { useVenue } from "providers/venue"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import { useFeatureQuickSearch } from "./useFeatureQuickSearch"
import { IFeatureCategoryObj } from "./useFeatureQuickSearch/types"
import { ImdfFeature, LevelData } from "providers/venue/types"

export interface IUseFeatureCategoryProps {
  initialVenueId?: string
  initialOrdinal?: number
  // Opt-in: hide floors with no matching feature from quickSearchVenueLevels
  // and only correct ordinals onto floors that do. Off by default so existing
  // tenants are unaffected; enable per-tenant until this becomes the default.
  filterLevelsByCategory?: boolean
}

export type ToggleQuickSearchFn = (
  type: string,
  featureCategory: string
) => void
export type HandleClickQuickSearchFn = (
  featureType: string,
  category: string
) => void
export type UseFeatureCategory = (props?: IUseFeatureCategoryProps) => {
  preFilterReady: boolean
  viewingCategory: string
  featureCategories: IFeatureCategoryObj[]
  searchableFeatures: ImdfFeature[]
  quickSearchVenueLevels: LevelData[]
  viewingOrdinal: string | number
  viewingVenueId: string
  comingFromOrdinal: string | number
  toggleQuickSearch: ToggleQuickSearchFn
  handleClickQuickSearch: HandleClickQuickSearchFn
  handleOrdinalChange: (data: { ordinal: number; venueId: string }) => void
}

export const useFeatureCategory: UseFeatureCategory = ({
  initialVenueId,
  initialOrdinal,
  filterLevelsByCategory = false,
}) => {
  const { category: viewingCategory } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [preFilterReady, setPreFilterReady] = useState<boolean>(false)
  const { dataLoaded, levels, isMultiVenueProject } = useVenue()

  const { viewingVenue: viewingVenueId, changeViewingVenue } =
    useVenueController(initialVenueId)
  const { categories: featureCategories, features: searchableFeatures } =
    useFeatureQuickSearch(viewingCategory, viewingVenueId)

  // Floors that actually have a feature matching the selected category —
  // used to keep the ordinal correction below in sync with what the floor
  // selector on the page renders (see useMap.js's quickSearchVenueLevels).
  const quickSearchVenueLevels = useMemo(() => {
    const sortedLevels = orderBy(levels, "properties.ordinal", "desc")
    const venueLevels = getVenueLevels(
      sortedLevels,
      viewingVenueId,
      isMultiVenueProject
    )
    return filterLevelsByCategory && viewingCategory
      ? filterLevelsByFeatureOrdinals(venueLevels, searchableFeatures)
      : venueLevels
  }, [
    levels,
    viewingVenueId,
    isMultiVenueProject,
    filterLevelsByCategory,
    viewingCategory,
    searchableFeatures,
  ])

  const viewingOrdinal = useMemo(
    () => searchParams.get(Q_ORDINAL),
    [searchParams]
  )

  const comingFromOrdinal = useMemo(
    () => searchParams.get(Q_FROM_ORDINAL),
    [searchParams]
  )

  const toggleQuickSearch = useCallback(
    (type, featureCategory) => {
      //This condition prevent navigation when viewingOrdinal or viewingVenueId is undefined
      const hasEmptyValue = [viewingVenueId, viewingOrdinal].some(isNil)
      if (hasEmptyValue) return
      if (featureCategory === viewingCategory) {
        return navigate(
          `/maps/ordinal/${viewingOrdinal}?venue=${viewingVenueId}`
        )
      }
      navigate(
        `/maps/${type}/${featureCategory}?venue=${viewingVenueId}&from-ordinal=${viewingOrdinal}`
      )
    },
    [navigate, viewingOrdinal, viewingCategory, viewingVenueId]
  )

  const handleClickQuickSearch = useCallback(
    (featureType, category) => {
      const pluralType = pluralize(featureType)
      toggleQuickSearch(pluralType, category)
    },
    [toggleQuickSearch]
  )

  const handleOrdinalChange = useCallback(
    ({ ordinal, venueId }) => {
      searchParams.set(Q_ORDINAL, ordinal)
      searchParams.set(Q_VENUE, venueId)
      setSearchParams(searchParams.toString(), {
        replace: true,
      })
    },
    [searchParams, setSearchParams]
  )

  const findNearestOrdinal = useCallback(
    (viewingOrdinal) => {
      const parsedOrdinal = !isNil(viewingOrdinal) ? Number(viewingOrdinal) : 0
      let nearestOrdinal = parsedOrdinal
      // Only consider features on a floor that is actually visible in the selector.
      const candidateFeatures = filterLevelsByCategory
        ? searchableFeatures.filter((feature) =>
            quickSearchVenueLevels
              .map((level) => getFeatureOrdinal(level))
              .includes(getFeatureOrdinal(feature))
          )
        : searchableFeatures
      const nearestFeature = minBy(candidateFeatures, (amenity) =>
        Math.abs(amenity.properties.ordinal - parsedOrdinal)
      )
      if (nearestFeature) {
        nearestOrdinal = nearestFeature.properties.ordinal
      }

      return nearestOrdinal
    },
    [searchableFeatures, quickSearchVenueLevels, filterLevelsByCategory]
  )

  // Runs on initial page load
  useEffect(() => {
    // Skip effect if ordinal and venue are already set on page reload
    if (preFilterReady || !dataLoaded) return

    // Skip effect without ordinal query string
    if (isNil(viewingOrdinal)) return

    const currentVenueIdFilterInPage = searchParams.get(Q_VENUE)
    changeViewingVenue(currentVenueIdFilterInPage)

    // Deep link landed on a category page with a floor that has no matching
    // amenity (e.g. shared/bookmarked URL) - correct to the nearest floor
    // that does, same as the "coming from quick search" path below.
    const isOrdinalVisible = quickSearchVenueLevels.some(
      (level) => `${getFeatureOrdinal(level)}` === `${viewingOrdinal}`
    )
    const correctedOrdinal =
      filterLevelsByCategory && viewingCategory && !isOrdinalVisible
        ? findNearestOrdinal(viewingOrdinal)?.toString()
        : viewingOrdinal

    // Merge rather than replace: an absolute write would drop unrelated params
    // (e.g. `search=1`), and `setSearchParams`'s identity changes on every URL
    // update, so this effect could re-fire on its own writes.
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set(Q_VENUE, currentVenueIdFilterInPage)
    nextParams.set(Q_ORDINAL, correctedOrdinal)
    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true })
    }
    setPreFilterReady(true)
  }, [
    searchParams,
    changeViewingVenue,
    dataLoaded,
    preFilterReady,
    viewingOrdinal,
    viewingVenueId,
    viewingCategory,
    quickSearchVenueLevels,
    filterLevelsByCategory,
    findNearestOrdinal,
    setSearchParams,
    setPreFilterReady,
  ])

  // Runs when coming from Amenity Quick Search
  useEffect(() => {
    if (!viewingVenueId || !dataLoaded || viewingOrdinal) return

    /**
     * Find the nearest ordinal for amenity:
     * - If `comingFromOrdinal` is present, use it (applies when navigating from another page).
     * - If `viewingOrdinal` is missing and `comingFromOrdinal` is absent, but `initialOrdinal` exists, use `locationOrdinal`.
     * - Otherwise, default to 0 (GF).
     */
    const currentOrdinal = comingFromOrdinal || initialOrdinal || 0
    const nearestOrdinal = findNearestOrdinal(currentOrdinal)?.toString()

    // Merge rather than replace: an absolute write would drop unrelated params
    // (e.g. `search=1`), and `setSearchParams`'s identity changes on every URL
    // update, so this effect could re-fire on its own writes.
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set(Q_VENUE, viewingVenueId)
    nextParams.set(Q_ORDINAL, nearestOrdinal)
    nextParams.delete(Q_FROM_ORDINAL)
    if (nextParams.toString() === searchParams.toString()) return

    setSearchParams(nextParams, { replace: true })
  }, [
    dataLoaded,
    viewingCategory,
    comingFromOrdinal,
    viewingVenueId,
    viewingOrdinal,
    initialOrdinal,
    findNearestOrdinal,
    searchParams,
    setSearchParams,
  ])

  return {
    preFilterReady,
    featureCategories,
    searchableFeatures,
    quickSearchVenueLevels,
    viewingCategory,
    viewingOrdinal,
    viewingVenueId,
    comingFromOrdinal,
    toggleQuickSearch,
    handleClickQuickSearch,
    handleOrdinalChange,
  }
}
